import { isNotNullOrUndefined, isNullOrUndefined, SerializedData } from 'in-time-core';
import { CloudFunctionsException } from './cloud-functions-exception.js';
import { HttpClient, HttpClientResponse } from './https-client.js';
import { logError } from 'in-time-logger';

export enum SupportedMethod { GET, PATCH, POST }

export class HttpsCallableResult {
  constructor(
    public readonly data: SerializedData,
  ) {}
}

export class HttpsAuthContext {
  constructor(
    public readonly accessToken: string | null,
  ) {}
}

export class HttpsCallable {
  constructor(
    public readonly httpClient: HttpClient,
    public readonly endpoint: string,
    public readonly context: HttpsAuthContext,
    public readonly metadata: SerializedData | null = null,
  ) {}

  async call(args?: { parameters?: SerializedData, headers?: Map<string, string>, method?: SupportedMethod}): Promise<HttpsCallableResult> {
    const requestHeaders: Map<string, string> = new Map<string, string>();
    if(isNotNullOrUndefined(args) && isNotNullOrUndefined(args.headers)) {
      for(const [key, value] of args.headers) {
        requestHeaders.set(key, value);
      }
    }

    requestHeaders.set('content-type', 'application/json; charset=utf-8');
    requestHeaders.set('accept', 'application/json; charset=utf-8');

    if(isNotNullOrUndefined(this.context.accessToken)) {
      requestHeaders.set('authorization', `Bearer ${this.context.accessToken}`);
    }
    else {
      requestHeaders.delete('authorization');
    }

    const requestBody: SerializedData = {
      'data': args?.parameters ?? {},
      'metadata': this.metadata,
    };

    try {
      const response = await this.getResponseByMethod(this.endpoint, requestHeaders, requestBody, args?.method);
      if(response.statusCode != 200) {
        throw new CloudFunctionsException(
            `HTTP_ERROR_${response.statusCode}`,
            `Http call failed with status code ${response.statusCode}`
        );
      }

      const contentType = response.headers.get('content-type');
      if(isNullOrUndefined(contentType) || !contentType.includes('application/json')) {
        throw new CloudFunctionsException(
            'HTTP_ERROR_CONTENT',
            `Invalid content type in http response: ${contentType}`
        );
      }

      if(isNullOrUndefined(response.data)) {
        throw new CloudFunctionsException(
            `HTTP_ERROR_MISSING_DATA`,
            `Invalid body in http response: Body is null or undefined`
        );
      }

      return new HttpsCallableResult(response.data);
    }
    catch(error) {
      if(error instanceof CloudFunctionsException) {
        throw error;
      }

      logError(`HttpsCallable failed to call endpoint {${this.endpoint}} with error: ${error}`);
      throw new CloudFunctionsException('HTTP_ERROR_INTERNAL', 'Internal http error', error);
    }
  }

  private getResponseByMethod(endpoint: string, headers: Map<string, string>, body: SerializedData, method?: SupportedMethod): Promise<HttpClientResponse> {
    switch(method) {
      case SupportedMethod.GET:
        return this.httpClient.get(endpoint, headers, body);
      case SupportedMethod.PATCH:
        return this.httpClient.patch(endpoint, headers, body);
      default:
        return this.httpClient.post(endpoint, headers, body);
    }
  }
}
